import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, Sparkles, Clock, AlertCircle, User } from "lucide-react";
import { useVision360Query, useVision360Regenerate } from "@/hooks/useVision360";
import { Vision360BodyMap } from "./Vision360BodyMap";
import { Vision360Summary } from "./Vision360Summary";
import { Vision360Medications } from "./Vision360Medications";
import { Vision360KeyMetrics } from "./Vision360KeyMetrics";
import { Vision360MarkerDetail } from "./Vision360MarkerDetail";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { BodyMarker } from "@/types/vision360";
import { DataStaleBanner } from "../DataStaleBanner";
import { useStaleFlags, useDataStale } from "@/contexts/DataStaleContext";

interface PatientVision360Props {
  patientId: string;
  patientSex?: "M" | "F" | string | null;
}

export function PatientVision360({ patientId, patientSex }: PatientVision360Props) {
  const [selectedMarker, setSelectedMarker] = useState<BodyMarker | null>(null);
  
  const { data, isLoading, error, refetch } = useVision360Query(patientId);
  const regenerate = useVision360Regenerate();
  const { clearVision360Stale } = useDataStale();
  useStaleFlags(patientId); // Carregar flags do banco

  const handleRegenerate = async () => {
    try {
      await regenerate.mutateAsync(patientId);
      // Limpar apenas o flag da visão 360 após regeneração bem-sucedida
      await clearVision360Stale(patientId);
      toast.success("Visão 360 atualizada com sucesso!");
    } catch (err) {
      console.error("[Vision360] Regenerate error:", err);
      toast.error("Erro ao atualizar Visão 360");
    }
  };

  const handleMarkerClick = (marker: BodyMarker) => {
    setSelectedMarker(marker);
  };

  if (isLoading) {
    return <Vision360Loading />;
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-3">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
          <p className="text-muted-foreground">Erro ao carregar Visão 360</p>
          <Button variant="outline" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </div>
      </Card>
    );
  }

  const snapshot = data?.snapshot;
  const hasData = snapshot && (
    snapshot.bodyMarkers?.length > 0 ||
    snapshot.topIssues?.length > 0 ||
    snapshot.medications?.length > 0 ||
    snapshot.keySummary?.length > 0
  );

  if (!hasData && !regenerate.isPending) {
    return (
      <Card className="p-6">
        <div className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <User className="h-8 w-8 text-muted-foreground" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">Visão 360 não disponível</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Ainda não há dados suficientes para gerar a visão completa do paciente.
            </p>
          </div>
          <Button 
            onClick={handleRegenerate} 
            disabled={regenerate.isPending}
            className="gap-2"
          >
            {regenerate.isPending ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Gerar Visão 360
              </>
            )}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Banner de dados desatualizados */}
      <DataStaleBanner
        patientId={patientId}
        type="vision360"
        onUpdate={handleRegenerate}
        isUpdating={regenerate.isPending}
      />

      {/* Header com metadata */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1">
            <Sparkles className="h-3 w-3" />
            Gerado por IA
          </Badge>
          {snapshot?.confidence && (
            <Badge variant="outline" className="gap-1">
              Confiança: {Math.round(snapshot.confidence * 100)}%
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {data?.generated_at && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Atualizado em {new Date(data.generated_at).toLocaleString("pt-BR")}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRegenerate}
            disabled={regenerate.isPending}
            className="gap-1"
          >
            <RefreshCw className={cn("h-4 w-4", regenerate.isPending && "animate-spin")} />
            {regenerate.isPending ? "Atualizando..." : "Atualizar"}
          </Button>
        </div>
      </div>

      {/* Layout principal */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Body Map */}
        <Card className="lg:col-span-4 p-4">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
            <User className="h-4 w-4" />
            Mapa Corporal
            {snapshot?.bodyMarkers && snapshot.bodyMarkers.length > 0 && (
              <Badge variant="secondary" className="ml-auto">
                {snapshot.bodyMarkers.length} marcador{snapshot.bodyMarkers.length !== 1 ? "es" : ""}
              </Badge>
            )}
          </h3>
          <Vision360BodyMap
            markers={snapshot?.bodyMarkers || []}
            sex={patientSex}
            onMarkerClick={handleMarkerClick}
          />
        </Card>

        {/* Resumo e Problemas */}
        <Card className="lg:col-span-4 p-4">
          <Vision360Summary
            keySummary={snapshot?.keySummary || []}
            topIssues={snapshot?.topIssues || []}
            alerts={snapshot?.alerts || []}
          />
        </Card>

        {/* Medicamentos e Métricas */}
        <Card className="lg:col-span-4 p-4 space-y-4">
          <Vision360Medications medications={snapshot?.medications || []} />
          <div className="border-t pt-4">
            <Vision360KeyMetrics metrics={snapshot?.keyMetrics || {}} />
          </div>
        </Card>
      </div>

      {/* Modal de detalhe do marcador */}
      {selectedMarker && (
        <Vision360MarkerDetail
          marker={selectedMarker}
          open={!!selectedMarker}
          onClose={() => setSelectedMarker(null)}
        />
      )}
    </div>
  );
}

function Vision360Loading() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-24" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4 p-4">
          <Skeleton className="h-[400px] w-full" />
        </Card>
        <Card className="lg:col-span-4 p-4 space-y-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </Card>
        <Card className="lg:col-span-4 p-4 space-y-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </Card>
      </div>
    </div>
  );
}
